import { CallLogResponse } from "@/dtos/CallLog.dto";

/**
 * Transcript JSON response structure from the API
 */
interface TranscriptResponse {
  callSid: string;
  text: string;
}

/**
 * Formats transcript text with proper line breaks and role labels
 * @param text - Raw transcript text with role prefixes
 * @returns Formatted transcript with clear role labels and line breaks
 */
export function formatTranscriptText(text: string): string {
  if (!text) return "";

  // Split on role prefixes while keeping them
  const lines = text.split(/(?=Customer:|Sales Rep:)/);

  return lines
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return "";

      // Ensure proper formatting with line breaks
      if (trimmed.startsWith("Customer:")) {
        return `Customer: ${trimmed.replace(/^Customer:\s*/, "").trim()}`;
      } else if (trimmed.startsWith("Sales Rep:")) {
        return `Sales Rep: ${trimmed.replace(/^Sales Rep:\s*/, "").trim()}`;
      }

      return trimmed;
    })
    .filter((line) => line.length > 0)
    .join("\n\n");
}

/**
 * Generates a filename for the transcript download
 * Format: transcript-YYYY-MM-DD-HH-mm.txt
 * @param callLog - Optional call log data to extract date from
 * @returns Formatted filename string
 */
export function generateTranscriptFilename(callLog?: CallLogResponse): string {
  let date: Date;

  if (callLog?.createdAt) {
    date = new Date(callLog.createdAt);
  } else {
    date = new Date();
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `transcript-${year}-${month}-${day}-${hours}-${minutes}.txt`;
}

/**
 * Downloads a transcript from the given URL as a formatted text file
 * @param transcriptUrl - URL to fetch the transcript JSON from
 * @param callLog - Optional call log data for filename generation
 * @throws Error if download fails
 */
export async function downloadTranscript(
  transcriptUrl: string,
  callLog?: CallLogResponse
): Promise<void> {
  if (!transcriptUrl) {
    throw new Error("Transcript URL is required");
  }

  try {
    // Fetch the transcript JSON
    const response = await fetch(transcriptUrl);

    if (!response.ok) {
      if (response.status === 403 || response.status === 401) {
        throw new Error("Transcript URL has expired. Please refresh the page.");
      }
      throw new Error(`Failed to fetch transcript: ${response.statusText}`);
    }

    // Parse JSON response
    const data: TranscriptResponse = await response.json();

    if (!data || typeof data.text !== "string") {
      throw new Error("Invalid transcript format: missing text field");
    }

    // Format the transcript text
    const formattedText = formatTranscriptText(data.text);

    // Generate filename
    const filename = generateTranscriptFilename(callLog);

    // Create blob and download
    const blob = new Blob([formattedText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw new Error("Network error: Unable to fetch transcript. Please check your connection.");
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("An unexpected error occurred while downloading the transcript.");
  }
}
